import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Title from '../../components/Title/Title';
import Button from '../../components/Button/Button';
import Input from '../../components/Input/Input';
import { useAuth } from '../../hooks/useAuth';
import useDarkOffersBackground from '../../hooks/useDarkOffersBackground';
import { isProfileComplete, COUNTRY_OPTIONS } from '../../utils/profile';
import CategorySelector from '../Offers/CategorySelector';
import classes from './profileSetup.module.css';

const currentYear = new Date().getFullYear();

export default function ProfileSetup() {
  useDarkOffersBackground();
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnUrl = params.get('returnUrl') || '/tasks';

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      birthDay: user?.birthDay || '',
      birthMonth: user?.birthMonth || '',
      birthYear: user?.birthYear || '',
      gender: user?.gender || '',
      countryCode: user?.countryCode || 'US',
      zipCode: user?.zipCode || '',
    },
  });

  useEffect(() => {
    if (!user) navigate(`/login?returnUrl=${encodeURIComponent('/profile-setup')}`);
  }, [user, navigate]);

  const submit = async (data) => {
    const payload = {
      birthDay: Number(data.birthDay),
      birthMonth: Number(data.birthMonth),
      birthYear: Number(data.birthYear),
      gender: data.gender,
      countryCode: data.countryCode,
      zipCode: String(data.zipCode).trim(),
    };
    const updated = await updateProfile(payload);
    if (isProfileComplete(updated)) navigate(returnUrl);
  };

  return (
    <div className={classes.container}>
      <CategorySelector />
      <div className={classes.details}>
        <Title title="Help us find surveys you'll qualify for" margin="1rem" color="#fff" />
        <p className={classes.subtitle}>
          Tell us a little about yourself so we can match you with surveys you’ll actually qualify for.
        </p>
        <form onSubmit={handleSubmit(submit)} noValidate>
          <div className={classes.row}>
            <label className={classes.field}>
              <span>Birth month</span>
              <select
                {...register('birthMonth', { required: 'Required', min: { value: 1, message: 'Invalid' }, max: { value: 12, message: 'Invalid' } })}
              >
                <option value="">MM</option>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              {errors.birthMonth && <em className={classes.error}>{errors.birthMonth.message}</em>}
            </label>
            <label className={classes.field}>
              <span>Birth day</span>
              <select
                {...register('birthDay', { required: 'Required', min: { value: 1, message: 'Invalid' }, max: { value: 31, message: 'Invalid' } })}
              >
                <option value="">DD</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              {errors.birthDay && <em className={classes.error}>{errors.birthDay.message}</em>}
            </label>
            <label className={classes.field}>
              <span>Birth year</span>
              <input
                type="number"
                placeholder="YYYY"
                {...register('birthYear', {
                  required: 'Required',
                  min: { value: 1900, message: 'Invalid year' },
                  max: { value: currentYear, message: 'Invalid year' },
                  validate: (v) => (currentYear - Number(v) >= 13 ? true : 'Must be 13 or older'),
                })}
              />
              {errors.birthYear && <em className={classes.error}>{errors.birthYear.message}</em>}
            </label>
          </div>

          <label className={classes.field}>
            <span>Gender</span>
            <select {...register('gender', { required: 'Please select' })}>
              <option value="">Select</option>
              <option value="m">Male</option>
              <option value="f">Female</option>
            </select>
            {errors.gender && <em className={classes.error}>{errors.gender.message}</em>}
          </label>

          <label className={classes.field}>
            <span>Country</span>
            <select {...register('countryCode', { required: 'Please select' })}>
              <option value="">Select</option>
              {COUNTRY_OPTIONS.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
            {errors.countryCode && <em className={classes.error}>{errors.countryCode.message}</em>}
          </label>

          <Input
            type="text"
            label="ZIP / Postal code"
            {...register('zipCode', {
              required: 'ZIP code is required',
              minLength: { value: 3, message: 'Too short' },
              maxLength: { value: 12, message: 'Too long' },
            })}
            error={errors.zipCode}
          />

          <Button
            type="submit"
            text={isSubmitting ? 'Saving...' : 'Save & Find Surveys'}
            color="#1a0b00"
            backgroundColor="#ffc93a"
          />
        </form>
      </div>
    </div>
  );
}
